import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  InputGroup,
  FormControl,
  Badge,
} from "react-bootstrap";
import {
  createTask,
  deleteTask,
  getTasks,
  updateTask,
  assignTaskToStudents,
  TASK_PRIORITY,
} from "../services/taskService";
import { getStudents } from "../services/userService";
import { getCourses } from "../services/courseService";
import { getProjects } from "../services/projectService";
import { getSkills } from "../services/skillService";
import { useAuth } from "../context/AuthProvider";
import Loader from "../components/Loader";
import { Eye, PencilIcon, TrashIcon } from "lucide-react";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { logActivity } from "../services/activityService";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import TaskDescriptionEditor from "../components/TaskDescriptionEditor";
import TaskDescriptionView from "../components/TaskDescriptionView";

function Tasks() {
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({
    name: "",
    description: "",
    projectId: "", // Add project selection
    projectName: "", // Store project name
    priority: TASK_PRIORITY.MEDIUM, // Add priority field
    skills: [],
  });
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortAsc, setSortAsc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [skills, setSkills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState(""); // Add project filter
  const [priorityFilter, setPriorityFilter] = useState(""); // Add priority filter
  const [skillFilter, setSkillFilter] = useState("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [taskImageFile, setTaskImageFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const maxTaskImageSize = 5 * 1024 * 1024;

  useEffect(() => {
    fetchTasks();
    fetchStudents();
    fetchCourses();
    fetchSkills();
    fetchProjects();
    if (user?.role === 'admin') {
      fetchTutors();
    }
  }, []);

  const fetchTutors = async () => {
    const { getTutors } = await import('../services/userService');
    const data = await getTutors();
    setTutors(data);
  };

  const closeAddEditModal = () => setShowAddEditModal(false);
  const closeAssignTaskModal = () => {
    setShowAssignModal(false);
    setSelectedStudent(null);
    setDueDate("");
  };

  const handleAssign = async () => {
    const payload = {
      taskId: selectedTask.id,
      taskName: selectedTask.name,
      taskDescription: selectedTask.description,
      userId: selectedStudent.id,
      userName: selectedStudent.name,
      assigneeId: user.id,
      assigneeName: user.name,
      dueDate,
      projectId: selectedTask.projectId || null,
      projectName: selectedTask.projectName || null,
      priority: selectedTask.priority || TASK_PRIORITY.MEDIUM,
      skills: getTaskSkills(selectedTask),
      taskImageUrl: selectedTask.taskImageUrl || null,
      taskImageName: selectedTask.taskImageName || null,
    };
    setBtnLoading(true);
    await assignTaskToStudents(payload);
    setBtnLoading(false);
    closeAssignTaskModal();
    toast.success("Task assigned successfully");
    addToRecentActivity();
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p.id === projectId);

    setTaskForm((prev) => ({
      ...prev,
      projectId: projectId,
      projectName: project ? project.name : "",
    }));
  };

  const addToRecentActivity = () => {
    const activity = {
      userId: user.id,
      userRole: user.role,
      targetUserId: selectedStudent.id,
      type: "task",
      text: `${user.name} assigned task "${selectedTask.name}"`,
      actionType: "task_assigned",
      metadata: {
        userName: user.name,
        taskId: selectedTask.id,
        taskName: selectedTask.name,
        targetUserId: selectedStudent.id,
        targetUserName: selectedStudent.name,
      },
    };
    logActivity(activity).catch((err) => console.error(err));
  };

  const openAssignModal = (task) => {
    setSelectedTask(task);
    setShowAssignModal(true);
  };

  const openDetailsModal = (task) => {
    setSelectedTask(task);
    setShowDetailsModal(true);
  };

  const fetchCourses = async () => {
    const data = await getCourses();
    setCourses(data);
  };

  const fetchSkills = async () => {
    const data = await getSkills();
    data.sort((a, b) => a.name.localeCompare(b.name));
    setSkills(data);
  };

  const fetchStudents = async () => {
    const data = await getStudents();
    setStudents(data);
  };

  const fetchProjects = async () => {
    const data = await getProjects();
    setProjects(data);
  };

  const fetchTasks = async () => {
    setLoading(true);
    const data = await getTasks();
    setTasks(data);
    setLoading(false);
  };

  const emptyTaskForm = {
    name: "",
    description: "",
    projectId: "",
    projectName: "",
    priority: TASK_PRIORITY.MEDIUM,
    skills: [],
  };

  const resetTaskForm = () => {
    setTaskForm(emptyTaskForm);
    setTaskImageFile(null);
    setFileInputKey(Date.now());
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const uploadTaskImage = async (file) => {
    if (!file) return null;
    if (!user?.id) throw new Error("Please login again to upload task images.");
    if (!file.type.startsWith("image/")) {
      throw new Error("Please select an image file.");
    }
    if (file.size > maxTaskImageSize) {
      throw new Error("Image is too large. Please upload an image up to 5 MB.");
    }

    const uploadTaskImageFn = httpsCallable(functions, "uploadTaskImage");
    const fileBase64 = await fileToBase64(file);
    const response = await uploadTaskImageFn({
      userId: user.id,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      fileBase64,
    });

    return response.data;
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    try {
      const imageData = await uploadTaskImage(taskImageFile);
      const payload = {
        ...taskForm,
        ...(imageData || {}),
      };
      const taskId = await createTask(payload);
      setTasks([{ ...payload, id: taskId, createdAt: Date.now() }, ...tasks]);
      resetTaskForm();
      closeAddEditModal();
      toast.success("Task added successfully");
    } catch (error) {
      console.error("Error adding task:", error);
      toast.error(error.message || "Failed to add task");
    }
    setBtnLoading(false);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    try {
      const imageData = await uploadTaskImage(taskImageFile);
      const payload = {
        ...taskForm,
        ...(imageData || {}),
      };
      await updateTask(taskForm.id, payload);
      setTasks(tasks.map((t) => (t.id === taskForm.id ? { ...payload, id: taskForm.id } : t)));
      resetTaskForm();
      setIsEditing(false);
      closeAddEditModal();
      toast.success("Task updated successfully");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(error.message || "Failed to update task");
    }
    setBtnLoading(false);
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    const task = tasks.find((t) => t.id === id);
    if (task) {
      setIsEditing(true);
      setTaskForm({ ...task, skills: Array.isArray(task.skills) ? task.skills : [] });
      setTaskImageFile(null);
      setFileInputKey(Date.now());
      setShowAddEditModal(true);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);
    await deleteTask(id);
    setTasks(tasks.filter((task) => task.id !== id));
    setLoading(false);
    toast.success("Task deleted successfully");
  };

  const getSkillId = (skill) => (typeof skill === "string" ? skill : skill?.id);
  const getSkillName = (skill) =>
    typeof skill === "string" ? skill : skill?.name || skill?.id || "";
  const normalizeValue = (value) => String(value || "").trim().toLowerCase();
  const normalizeSkill = (skill) => normalizeValue(getSkillName(skill));

  const coursesById = courses.reduce((acc, course) => {
    acc[normalizeValue(course.id)] = course;
    return acc;
  }, {});

  const getTaskCourse = (task) => {
    const taskCourseId = task.courseId || task.course?.id || task.course;
    const taskCourseName = task.courseName || task.course?.name;

    if (taskCourseId && coursesById[normalizeValue(taskCourseId)]) {
      return coursesById[normalizeValue(taskCourseId)];
    }

    return courses.find((course) => {
      const sameName =
        taskCourseName &&
        normalizeValue(course.name) === normalizeValue(taskCourseName);

      return sameName;
    });
  };

  const getTaskSkills = (task) => {
    const directSkills = Array.isArray(task.skills) ? task.skills : [];
    if (directSkills.length > 0) return directSkills;

    const courseSkills = getTaskCourse(task)?.skills || [];

    return Array.isArray(courseSkills) ? courseSkills : [];
  };

  const availableSkills = [
    ...skills,
    ...tasks.flatMap((task) => (Array.isArray(task.skills) ? task.skills : [])),
    ...courses.flatMap((course) =>
      Array.isArray(course.skills) ? course.skills : []
    ),
  ]
    .filter(
      (skill, index, allSkills) =>
        getSkillName(skill) &&
        allSkills.findIndex(
          (item) => normalizeSkill(item) === normalizeSkill(skill)
        ) === index
    )
    .sort((a, b) => getSkillName(a).localeCompare(getSkillName(b)));

  const filteredTasks = tasks
    .filter((t) => {
      const matchesSearch = Object.values(t).some(
        (value) =>
          value && value.toString().toLowerCase().includes(filter.toLowerCase())
      );

      let matchesProject = true;
      if (projectFilter) {
        if (projectFilter === "no-project") {
          matchesProject = !t.projectId;
        } else {
          matchesProject = t.projectId === projectFilter;
        }
      }

      let matchesPriority = true;
      if (priorityFilter) {
        matchesPriority = t.priority === priorityFilter;
      }

      let matchesSkill = true;
      if (skillFilter) {
        const selectedSkill = normalizeValue(skillFilter);
        matchesSkill = getTaskSkills(t).some(
          (skill) =>
            normalizeValue(getSkillId(skill)) === normalizeValue(skillFilter) ||
            normalizeSkill(skill) === selectedSkill
        );
      }

      return matchesSearch && matchesProject && matchesPriority && matchesSkill;
    })
    .sort((a, b) => {
      if (sortKey === "createdAt") {
        const aValue = Number(a.createdAt || 0);
        const bValue = Number(b.createdAt || 0);
        return sortAsc ? aValue - bValue : bValue - aValue;
      }

      const aValue = a[sortKey] ? a[sortKey].toString().toLowerCase() : "";
      const bValue = b[sortKey] ? b[sortKey].toString().toLowerCase() : "";
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
  const paginatedTasks = filteredTasks.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const updateFilter = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
      return;
    }

    setSortKey(key);
    setSortAsc(key === "createdAt" ? false : true);
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold">Task ({tasks.length})</h2>
      {loading && <Loader />}
      <div className="flex justify-between items-center gap-4 mb-4">
        <InputGroup className="flex-1">
          <FormControl
            placeholder="Search tasks..."
            value={filter}
            onChange={(e) => updateFilter(setFilter)(e.target.value)}
          />
        </InputGroup>

        <Form.Select
          className="w-auto"
          value={projectFilter}
          onChange={(e) => updateFilter(setProjectFilter)(e.target.value)}
        >
          <option value="">All Projects</option>
          <option value="no-project">No Project</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              🚀 {project.name}
            </option>
          ))}
        </Form.Select>

        <Form.Select
          className="w-auto"
          value={priorityFilter}
          onChange={(e) => updateFilter(setPriorityFilter)(e.target.value)}
        >
          <option value="">All Priorities</option>
          <option value={TASK_PRIORITY.LOW}>🟢 Low</option>
          <option value={TASK_PRIORITY.MEDIUM}>🟡 Medium</option>
          <option value={TASK_PRIORITY.HIGH}>🟠 High</option>
          <option value={TASK_PRIORITY.CRITICAL}>🔴 Critical</option>
        </Form.Select>

        <Button
          variant="success"
          className="flex-col"
          onClick={() => {
            setShowAddEditModal(true);
            setIsEditing(false);
            resetTaskForm();
          }}
        >
          + Add Task
        </Button>
      </div>

      {availableSkills.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="fw-semibold text-muted">Skills:</span>
          <Badge
            bg={!skillFilter ? "primary" : "light"}
            text={!skillFilter ? "light" : "dark"}
            role="button"
            className="px-3 py-2"
            onClick={() => updateFilter(setSkillFilter)("")}
          >
            All
          </Badge>
          {availableSkills.map((skill) => (
            <Badge
              key={getSkillId(skill) || getSkillName(skill)}
              bg={
                normalizeValue(skillFilter) === normalizeSkill(skill)
                  ? "primary"
                  : "secondary"
              }
              role="button"
              className="px-3 py-2"
              onClick={() => updateFilter(setSkillFilter)(getSkillName(skill))}
            >
              {getSkillName(skill)}
            </Badge>
          ))}
        </div>
      )}

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th onClick={() => handleSort("name")}>Task Name</th>
            <th onClick={() => handleSort("description")}>Description</th>
            <th>Project</th>
            <th>Skills</th>
            <th>Image</th>
            <th onClick={() => handleSort("priority")}>Priority</th>
            <th colSpan={2}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTasks.length === 0 && (
            <tr>
              <td colSpan="8" className="text-center">
                No tasks found.
              </td>
            </tr>
          )}
          {paginatedTasks.map((task, index) => (
            <tr key={task.id}>
              <td>{(currentPage - 1) * pageSize + index + 1}</td>
              <td>{task.name}</td>
              <td style={{ maxWidth: "320px" }}>
                <div
                  style={{
                    maxHeight: "112px",
                    overflow: "hidden",
                  }}
                >
                  <TaskDescriptionView text={task.description} preview />
                </div>
              </td>
              <td>
                {task.projectId
                  ? projects.find((p) => p.id === task.projectId)?.name ||
                    "Unknown Project"
                  : "No Project"}
              </td>
              <td>
                {getTaskSkills(task).length > 0
                  ? getTaskSkills(task).map((skill) => getSkillName(skill)).join(", ")
                  : "No Skills"}
              </td>
              <td>
                {task.taskImageUrl ? (
                  <a
                    href={task.taskImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={task.taskImageName || "task-image"}
                  >
                    Download
                  </a>
                ) : (
                  "No Image"
                )}
              </td>
              <td>
                <Badge
                  bg={
                    task.priority === TASK_PRIORITY.CRITICAL
                      ? "danger"
                      : task.priority === TASK_PRIORITY.HIGH
                      ? "warning"
                      : task.priority === TASK_PRIORITY.MEDIUM
                      ? "info"
                      : "success"
                  }
                  className="text-white"
                >
                  {task.priority === TASK_PRIORITY.CRITICAL && "🔴 Critical"}
                  {task.priority === TASK_PRIORITY.HIGH && "🟠 High"}
                  {task.priority === TASK_PRIORITY.MEDIUM && "🟡 Medium"}
                  {task.priority === TASK_PRIORITY.LOW && "🟢 Low"}
                  {!task.priority && "🟡 Medium"}
                </Badge>
              </td>
              <td>
                <div className="flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => openDetailsModal(task)}
                    title="View task details"
                  >
                    <Eye size={16} />
                  </Button>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => handleEdit(task.id)}
                  >
                    <PencilIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(task.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => openAssignModal(task)}
                  >
                    Assign
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      <Modal show={showAddEditModal} onHide={closeAddEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Task" : "Add New Task"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={isEditing ? handleUpdateTask : handleAddTask}>
            <Form.Group className="mb-3">
              <Form.Label>Task Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter task name"
                required
                value={taskForm.name}
                onChange={(e) =>
                  setTaskForm({ ...taskForm, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <TaskDescriptionEditor
                value={taskForm.description}
                onChange={(description) =>
                  setTaskForm({ ...taskForm, description })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Course</Form.Label>
              <Form.Select
                value={taskForm.courseId || ""}
                onChange={(e) =>
                  setTaskForm({ ...taskForm, courseId: e.target.value })
                }
                required
              >
                <option value="" disabled>
                  Select a course
                </option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Project (Optional)</Form.Label>
              <Form.Select
                value={taskForm.projectId || ""}
                onChange={handleProjectChange}
              >
                <option value="">No Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    🚀 {project.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Link this task to a project for better organization
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Skills</Form.Label>
              <div className="grid grid-cols-3 gap-2">
                {skills.map((skill) => (
                  <Form.Check
                    key={skill.id}
                    type="checkbox"
                    label={skill.name}
                    checked={(taskForm.skills || []).some(
                      (selectedSkill) =>
                        normalizeValue(getSkillId(selectedSkill)) === normalizeValue(skill.id) ||
                        normalizeSkill(selectedSkill) === normalizeSkill(skill)
                    )}
                    onChange={(e) =>
                      setTaskForm((prev) => ({
                        ...prev,
                        skills: e.target.checked
                          ? (prev.skills || []).some(
                              (selectedSkill) =>
                                normalizeValue(getSkillId(selectedSkill)) ===
                                  normalizeValue(skill.id) ||
                                normalizeSkill(selectedSkill) === normalizeSkill(skill)
                            )
                            ? prev.skills || []
                            : [...(prev.skills || []), skill]
                          : (prev.skills || []).filter(
                              (selectedSkill) =>
                                normalizeValue(getSkillId(selectedSkill)) !==
                                  normalizeValue(skill.id) &&
                                normalizeSkill(selectedSkill) !== normalizeSkill(skill)
                            ),
                      }))
                    }
                  />
                ))}
              </div>
              <Form.Text className="text-muted">
                Pick the specific skills this task practices.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Task Image</Form.Label>
              <Form.Control
                key={fileInputKey}
                type="file"
                accept="image/*"
                onChange={(e) => setTaskImageFile(e.target.files?.[0] || null)}
              />
              {taskForm.taskImageUrl && !taskImageFile && (
                <Form.Text className="text-muted">
                  Current image:{" "}
                  <a
                    href={taskForm.taskImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={taskForm.taskImageName || "task-image"}
                  >
                    {taskForm.taskImageName || "Download image"}
                  </a>
                </Form.Text>
              )}
              {taskImageFile && (
                <Form.Text className="text-muted">
                  Selected: {taskImageFile.name}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={taskForm.priority || TASK_PRIORITY.MEDIUM}
                onChange={(e) =>
                  setTaskForm({ ...taskForm, priority: e.target.value })
                }
                required
              >
                <option value={TASK_PRIORITY.LOW}>🟢 Low</option>
                <option value={TASK_PRIORITY.MEDIUM}>🟡 Medium</option>
                <option value={TASK_PRIORITY.HIGH}>🟠 High</option>
                <option value={TASK_PRIORITY.CRITICAL}>🔴 Critical</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Set the priority level for this task
              </Form.Text>
            </Form.Group>
            <Button type="submit" variant="primary" disabled={btnLoading}>
              {btnLoading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    className="text-white"
                  />
                  <span className="ml-2">
                    {isEditing ? "Updating..." : "Adding..."}
                  </span>
                </>
              ) : (
                <>{isEditing ? "Update Task" : "Add Task"}</>
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedTask?.name || "Task Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Badge
                  bg={
                    selectedTask.priority === TASK_PRIORITY.CRITICAL
                      ? "danger"
                      : selectedTask.priority === TASK_PRIORITY.HIGH
                      ? "warning"
                      : selectedTask.priority === TASK_PRIORITY.MEDIUM
                      ? "info"
                      : "success"
                  }
                  className="text-white"
                >
                  {(selectedTask.priority || TASK_PRIORITY.MEDIUM).toUpperCase()}
                </Badge>
                <Badge bg="secondary">
                  {selectedTask.projectId
                    ? projects.find((p) => p.id === selectedTask.projectId)?.name ||
                      selectedTask.projectName ||
                      "Project"
                    : "No Project"}
                </Badge>
              </div>

              <div className="mb-3">
                <h6 className="fw-semibold">Description</h6>
                <div className="border rounded p-3 bg-light">
                  <TaskDescriptionView text={selectedTask.description} />
                </div>
              </div>

              {getTaskSkills(selectedTask).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-semibold">Skills</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {getTaskSkills(selectedTask).map((skill) => (
                      <Badge key={getSkillId(skill) || getSkillName(skill)} bg="primary">
                        {getSkillName(skill)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {selectedTask.taskImageUrl && (
                <div>
                  <h6 className="fw-semibold">Attachment</h6>
                  <Button
                    as="a"
                    href={selectedTask.taskImageUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={selectedTask.taskImageName || "task-image"}
                    variant="outline-primary"
                    size="sm"
                  >
                    Download image
                  </Button>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedTask && (
            <Button
              variant="success"
              onClick={() => {
                setShowDetailsModal(false);
                openAssignModal(selectedTask);
              }}
            >
              Assign Task
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showAssignModal} onHide={closeAssignTaskModal}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-1">
            Task Name:{" "}
            <span className="font-semibold">{selectedTask?.name}</span>
          </div>
          <Form.Group>
            <Form.Label>Select Assignee</Form.Label>
            <Form.Select
              value={selectedStudent?.id || ""}
              onChange={(e) => {
                const [id, name] = e.target.value.split("|");
                setSelectedStudent({ id, name });
              }}
            >
              <option value="" disabled>
                Select a student or tutor
              </option>
              {/* Show tutors for Srikakulam Classifides (admin only) */}
              {user?.role === 'admin' && tutors.map((tutor) => (
                <option key={tutor.id} value={`${tutor.id}|${tutor.name}`}>{tutor.name} (Tutor)</option>
              ))}
              {students.map((student) => (
                <option key={student.id} value={`${student.id}|${student.name}`}>{student.name} (Student)</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAssignTaskModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedStudent || !dueDate}
          >
            {btnLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="text-white"
                />
                <span className="ml-2">Assigning...</span>
              </>
            ) : (
              "Assign Task"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Tasks;
